import { useState, useEffect, useMemo, useRef } from 'react';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { declarationVersionOf } from '@shared/utils/declarationVersion';
import { toMillisOrNull } from '@shared/utils/millis';
import {
  liveDeclarationTitle,
  liveGermanDeclarationTitle,
} from '@shared/utils/signedDocumentSource';
import type { TFunction } from 'i18next';
import { db, auth } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';
import type { Assignment, Child, FieldConfig, FieldValues, LegalBinding } from '@shared/types';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { getErrorCode, getErrorMessage } from '@shared/utils/errorUtils';
import { computeAge, isMinorForConsent, toBirthDateString } from '@shared/utils/consentAge';
import { appCheckedFetch } from '../../utils/appCheckedFetch';
import { isPerChildDeclaration } from '../../utils/perChildDeclarations';

/**
 * Replace system placeholders in declaration text with actual values.
 * System placeholders use format: {placeholder_name}
 * (Different from interactive field placeholders: {{field:fieldId}})
 */
function replaceSystemPlaceholders(
  text: string,
  parentName: string,
  childrenNames: string[],
  language: 'bg' | 'de' | 'en' = 'bg'
): string {
  const currentDate = new Date();

  // Format date based on language
  const dateFormatters: Record<string, Intl.DateTimeFormat> = {
    bg: new Intl.DateTimeFormat('bg-BG', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    de: new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    en: new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
  };
  const formattedDate = dateFormatters[language]?.format(currentDate) || currentDate.toLocaleDateString();

  // Get academic year (e.g., "2025/2026")
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const academicYear = month >= 8 ? `${year}/${year + 1}` : `${year - 1}/${year}`;

  // Format children names
  const childrenNamesStr = childrenNames.join(', ');

  return text
    .replace(/\{parent_name\}/g, parentName || '_______________')
    .replace(/\{selected_children_names\}/g, childrenNamesStr || '_______________')
    .replace(/\{current_date\}/g, formattedDate)
    .replace(/\{academic_year\}/g, academicYear);
}

/**
 * Грешката при подписване, преведена по КОД, а не по текст.
 *
 * 🔴 Защо съществува: `setError(getErrorMessage(err) || t('…signingFailed'))`
 * изглежда като преводим резерв и НЕ Е. `getErrorMessage` връща `error.message`
 * за всеки `Error`, а той никога не е празен по истински път — тоест десният
 * клон се задейства само при `new Error('')` и ключът е мъртъв. Ефектът пада
 * точно върху правилото, което задачата пази: откажеше ли сървърът подпис за
 * дете под прага, родителят виждаше `Parental consent is required for children
 * under 16 (GDPR Art. 8)` — на английски, на български и немски родители.
 *
 * Преводът е по код (`invalid-argument`, `not-found`, `failed-precondition`,
 * `permission-denied`, `internal`), защото текстът на сървъра е английски низ,
 * който се мени без предупреждение; съвпадение по него е чупливо по
 * конструкция. Сървърното съобщение остава като `defaultValue` — непознат код
 * дава английски текст, което е по-добро от видим ключ на екрана.
 *
 * ⚠️ Ограничение, обявено: `failed-precondition` покрива ДВА сървърни случая —
 * чл. 8 и „вече подписана" — и кодът не ги различава. Затова преводът му казва
 * и двете. Разделянето иска нов код от бекенда.
 *
 * 🔴 И то вече се случи веднъж, в грешната посока: задача 14 добави ТРЕТИ случай
 * (сблъсък на версии) под същия код, тоест родителят получаваше „вече е
 * подписана или липсва родителско съгласие" при сложени отметки — грешна причина
 * и грешно действие. Сега онзи случай има свой код (`aborted`).
 *
 * ⚠️ Затова: НОВ сървърен случай иска НОВ код И ключ в трите локала. Гардът е
 * `src/__tests__/infra/signingErrorCodesRatchet.test.ts` — той пада, ако код от
 * съюза на `CreateSignatureError` остане без превод.
 *
 * ⬜ Какво НЕ е поправено: ключът още печели над съобщението на сървъра. Това е
 * СЪЗНАТЕЛНО — сървърното съобщение не е преведено, тоест показано на родител на
 * друг език е по-лошо от общ, но преведен текст. Истинската поправка е сървърът
 * да праща КЛЮЧ, а не текст, и това е промяна на контракта на всички callable —
 * отделен кръг.
 */
function translateSigningError(err: unknown, t: TFunction): string {
  const serverMessage = getErrorMessage(err);
  const code = getErrorCode(err);
  if (code) {
    return t(`signModal.errors.server.${code}`, {
      defaultValue: serverMessage || t('signModal.errors.signingFailed'),
    });
  }
  return serverMessage || t('signModal.errors.signingFailed');
}

/**
 * Какво модалът още СЪБИРА като съгласие — вече само чл. 8.
 *
 * 🔴 `dataProcessing`, `emailCommunication` и `photoUse` са МАХНАТИ (Р-6.10/Р-6.2).
 * Първото беше условие да се подпише каквото и да е друго, тоест не беше свободно
 * дадено; другите две дублираха `PHOTO_CONSENT` и се презаписваха при всяко
 * подписване. Типовете в `shared/types` още ги описват и `signatureService`
 * още ги записва, АКО ги получи — заварените 1053 записа остават непокътнати
 * като одитна следа (Р-6.3). Оттук нататък просто не се изпращат.
 */
interface GdprConsent {
  /** GDPR Art. 8 — explicit parental consent when child is < 16 */
  parentalConsent: boolean;
}

interface UseSignModalProps {
  assignment: Assignment;
  children: Child[];
  onSuccess: () => void;
}

interface UseSignModalReturn {
  // State
  /** ЕФЕКТИВНИЯТ режим: за декларация с лични данни на детето (задача 41) е винаги `'single'`. */
  mode: 'all' | 'single';
  setMode: (mode: 'all' | 'single') => void;
  /** Предлага ли се „За всички деца" изобщо — не за декларации с лични данни на детето (задача 41). */
  allowAllChildren: boolean;
  selectedChildId: string | null;
  setSelectedChildId: (id: string | null) => void;
  signatureData: string;
  setSignatureData: (data: string) => void;
  signingMethod: 'canvas' | 'code';
  setSigningMethod: (method: 'canvas' | 'code') => void;
  verificationCode: string;
  enteredCode: string;
  setEnteredCode: (code: string) => void;
  gdprConsent: GdprConsent;
  setGdprConsent: (consent: GdprConsent) => void;
  loading: boolean;
  error: string;
  declarationText: string;
  /**
   * Заглавието на живата декларация, на езика на четеца — СЪЩАТА стойност, която
   * отива в `selectedTitleSnapshot` при подписване. Заглавката на модала показваше
   * `assignment.declarationTitle.bg`, тоест снимката отпреди преименуването;
   * родителят вижда точно каквото ще бъде записано под подписа му.
   */
  declarationTitleSnapshot: string;
  /** Версията на ПОКАЗАНИЯ текст — за разгръщане на `{{version}}` при рендер. */
  shownDeclarationVersion: number | undefined;
  /**
   * Кога е публикувана тя — в МИЛИСЕКУНДИ, не `Date`. Само за екрана; сървърът
   * я снима сам.
   *
   * 🔴 Числото не е стил, а поправка на цикъл. `new Date(...)` дава НОВ обект
   * при всяко изпълнение на ефекта, тоест `setState` вижда промяна дори когато
   * стойността е същата → нов рендер → ефектът пак → безкраен цикъл. Заварените
   * `setDeclarationText(...)` не го правеха, защото низът се сравнява по
   * СТОЙНОСТ и React спира сам.
   *
   * ⚠️ Излезе под тест (таймаут, не грешка), защото мокът на `useTranslation`
   * връща нова `t` при всяко викане, а ефектът я има в зависимостите си. В
   * production `t` е стабилна и цикълът би мълчал — тоест тестът хвана нещо,
   * което живото изпълнение крие.
   */
  shownVersionPublishedAtMillis: number | undefined;
  loadingDeclaration: boolean;
  /**
   * За кои деца декларацията ЧАКА подпис (задача 43) — `'loading'` · `'ready'` · `'failed'`.
   * 🔴 `'failed'` е „не знам", не „няма": изборът на деца не се показва и подписът е забранен.
   */
  pendingCheck: 'loading' | 'ready' | 'failed';
  /**
   * ИЗБРАНОТО дете е в семейството, но декларацията за него вече НЕ чака подпис (подписано преди
   * отварянето или междувременно). Задача 43 §2.2 — обяснението казва това, не „не е в списъка".
   */
  selectedChildNotPending: boolean;
  /**
   * `selectedChildNotPending` в режим „едно дете" — само тогава изборът е целта. Бутоните чакат
   * друг избор; известието под избора казва защо (едно място, без втори ред за грешка).
   */
  selectionNotPending: boolean;
  /**
   * Нищо за подписване оттук: списъкът с чакащите е празен (НЕ сечението с `children` — празно
   * сечение може да значи и „децата не са заредени", не знам ≠ няма), ИЛИ в режим „за всички"
   * нито едно от подадените деца не чака (чакащото е за дете без запис).
   */
  nothingPending: boolean;

  // Interactive Fields
  fieldsSchema: FieldConfig[] | null;
  fieldValues: FieldValues;
  setFieldValues: (values: FieldValues) => void;
  fieldErrors: Record<string, string>;

  // Scroll tracking
  hasScrolledToBottom: boolean;
  setHasScrolledToBottom: (value: boolean) => void;

  // Legal binding (bilingual)
  germanDeclarationText: string;
  legalDisclaimerAccepted: boolean;
  setLegalDisclaimerAccepted: (value: boolean) => void;
  currentLanguage: string;

  // Computed
  eligibleChildren: Child[];
  hasInteractiveFields: boolean;
  /**
   * True if ANY of the currently targeted children is under 16 (GDPR Art. 8).
   * Used to show a required parental-consent checkbox.
   */
  requiresParentalConsent: boolean;
  /** True if birthDate is missing for any targeted child (treated as minor — safer default). */
  hasUnknownAge: boolean;

  // Actions
  handleSign: (responseType: 'signed' | 'declined') => Promise<void>;
}

export function useSignModal({
  assignment,
  children,
  onSuccess,
}: UseSignModalProps): UseSignModalReturn {
  const { customClaims, userProfile, loading: authLoading } = useAuth();
  const { validateAll } = useFieldValidation();
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'bg').substring(0, 2) as 'bg' | 'de' | 'en';

  // Get parent name and children names for placeholder replacement
  const parentName = userProfile?.displayName || '';

  // State
  /**
   * 🔴 Формата е отворена за ЕДНО възлагане → подразбирането е ТОВА възлагане (39А §12).
   *
   * Дотук започваше с `'all'` и при „За едно дете" избираше ПЪРВОТО дете в списъка: от кутията
   * на дете X („подпишете … за X") подписът по подразбиране отиваше за всички, а при едно дете —
   * за друго, чието възлагане се подписваше, докато това на X оставаше. „За всички" остава избор
   * на родителя, не подразбиране.
   * ⚠️ Без ефект, който „поправя" избора при смяна на `children`: опресняването на списъка
   * нулираше ръчния избор към първото дете. Липсващото в списъка дете НЕ се заменя тихо —
   * `ChildSelector` показва видимо състояние, а `handleSign` отказва без цел.
   */
  const [mode, setModeState] = useState<'all' | 'single'>('single');
  /**
   * 🔴 Задача 41: медицинската декларация (и всяка от `PER_CHILD_DECLARATION_CODES`) носи данни,
   * ЛИЧНИ за детето — подписана „За всички", алергиите на едно дете влизат в спешните карти на
   * братята и сестрите му. Два гарда, всеки със своя задача:
   *   1. сетърът: `'all'` за такъв код НЕ се записва — радиото го няма, но и друг викач не може;
   *   2. ефективният режим: изборът на деца и върнатият `mode` са по него, тоест дори `'all'` да
   *      е останал в състоянието отнякъде (формата не е с ключ по възлагане — същата инстанция
   *      може да получи друго `assignment`), подписът тръгва само за едно дете.
   */
  const allowAllChildren = !isPerChildDeclaration(assignment.declarationCode);
  const setMode = (next: 'all' | 'single') => {
    if (next === 'all' && !allowAllChildren) return;
    setModeState(next);
  };
  const effectiveMode: 'all' | 'single' = allowAllChildren ? mode : 'single';
  const [selectedChildId, setSelectedChildId] = useState<string | null>(assignment.childId ?? null);
  const [signatureData, setSignatureData] = useState<string>('');
  const [signingMethod, setSigningMethod] = useState<'canvas' | 'code'>('canvas');
  const [verificationCode, setVerificationCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [gdprConsent, setGdprConsent] = useState<GdprConsent>({
    parentalConsent: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  /** Целите в режим „за всички“ се разминаха при подписа — списъкът се презарежда (задача 43). */
  const [selectionWentStale, setSelectionWentStale] = useState(false);
  /**
   * СУРОВИЯТ текст (с плейсхолдърите), както е дошъл от Firestore — или четимият резервен низ.
   * 🔴 Задача 43: попълненият текст (`declarationText`) е ПРОИЗВОДЕН от него и от ЦЕЛИТЕ на подписа —
   * смяната на избора го обновява без ново зареждане, а зареждането не зависи от имената.
   */
  const [rawDeclarationText, setRawDeclarationText] = useState<string>('');
  /**
   * Версията на текста, който е ПОКАЗАН на родителя.
   *
   * 🔴 Запомня се в момента на зареждане, не се чете при изпращане: между двете
   * админ може да е публикувал нова версия, а родителят е чел старата. Сървърът
   * сверява изпратената срещу живата и отказва при разминаване.
   */
  const [shownDeclarationVersion, setShownDeclarationVersion] = useState<number | undefined>(undefined);
  /**
   * Кога е публикувана ПОКАЗАНАТА версия.
   *
   * 🔴 Четем я, макар да НЕ я изпращаме: сървърът я снима сам от декларацията
   * (`signatureService`, при сверката на версията), защото на клиента не бива
   * да се вярва за съдържанието на документа. Тук тя служи само за ЕКРАНА —
   * без нея родителят би видял „версия 8", а документът „версия 8 от
   * 16.09.2026 г.", тоест разминаване в изречението, което той приема.
   */
  const [shownVersionPublishedAtMillis, setShownVersionPublishedAtMillis] =
    useState<number | undefined>(undefined);
  const [loadingText, setLoadingText] = useState(true);

  // Interactive Fields state
  const [fieldsSchema, setFieldsSchema] = useState<FieldConfig[] | null>(null);
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Scroll tracking state
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Legal binding (bilingual) state
  const [rawGermanText, setRawGermanText] = useState<string>('');
  /**
   * Заглавието, което стои над ТОЗИ текст — снима се заедно с него.
   *
   * 🔴 Не се чете от `assignment.declarationTitle`: то е денормализирано при
   * възлагането и е отделна стойност, която не се обновява при преименуване на
   * декларацията. Документът рисува заглавието на ДЕКЛАРАЦИЯТА, тоест снимката
   * трябва да идва от същия документ, от който идва и текстът.
   */
  const [declarationTitleSnapshot, setDeclarationTitleSnapshot] = useState<string>('');
  const [germanTitleSnapshot, setGermanTitleSnapshot] = useState<string>('');
  const [legalDisclaimerAccepted, setLegalDisclaimerAccepted] = useState(false);

  // Generate verification code
  useEffect(() => {
    const code = `${assignment.assignmentId.slice(0, 4)}-${Date.now().toString().slice(-4)}`.toUpperCase();
    setVerificationCode(code);
  }, [assignment.assignmentId]);

  // Load declaration text
  useEffect(() => {
    async function loadDeclaration() {
      /**
       * 🔴 Двата случая са РАЗЛИЧНИ и досега се третираха еднакво — тихо.
       *
       * Заедно те изгасваха „зарежда се", без да сложат текст: екранът показваше
       * празна декларация без никаква грешка, за разлика от другите три изхода
       * (`unavailable`, `notFound`, `loadError`), които слагат четим низ.
       */
      if (!assignment.declarationCode) {
        // Постоянно: няма код, следващ рендер няма да го донесе.
        setRawDeclarationText(t('signModal.declarationText.notFound'));
        setLoadingText(false);
        return;
      }
      if (!customClaims?.schoolId) {
        /**
         * 🔴 ДВЕ различни състояния, които първата поправка сля в едно.
         *
         * `authLoading` още тече → claims просто не са дошли. Чакаме: ефектът
         * се пуска наново и след частица от секундата текстът се зарежда
         * нормално. Изписването на съобщение тук би мигало при всяко отваряне.
         *
         * `authLoading` е приключил, а `schoolId` пак го няма → профилът не е
         * свързан с училище. Това е ОКОНЧАТЕЛНО и чакането е задънена улица:
         * екранът стои вечно „зарежда се", а бутонът за отказ чака точно него.
         * Тоест родителят не може нито да подпише, нито да откаже, и никъде не
         * пише защо.
         *
         * ⚠️ Сливането им беше същият клас като дефекта, който тази задача
         * поправя: по-рядкото състояние минава тихо.
         */
        if (authLoading) {
          return;
        }
        // ⚠️ Текстът на `noSchool` е ДУМА ПО ДУМА същият като
        // `signModal.errors.server.internal` („Възникна техническа грешка.
        // Опитайте отново или се свържете с училището.") — по решение на owner-а,
        // и преводите са взети от него, не написани наново.
        //
        // Отделен ключ, защото двата случая имат различни бъдещета: този тук е
        // състояние на екрана, другият е отговор на сървъра. Смени ли се единият,
        // другият НЕ се сменя сам — това е съзнателно, не пропуск.
        setRawDeclarationText(t('signModal.declarationText.noSchool'));
        setLoadingText(false);
        return;
      }

      try {
        // Try by document ID first
        const declarationRef = doc(
          db,
          `schools/${customClaims.schoolId}/declarations/${assignment.declarationCode}`
        );
        let declarationSnap = await getDoc(declarationRef);

        // If not found, try by code field
        if (!declarationSnap.exists()) {
          const declarationsRef = collection(
            db,
            `schools/${customClaims.schoolId}/declarations`
          );
          const declarationQuery = query(
            declarationsRef,
            where('code', '==', assignment.declarationCode),
            limit(1)
          );
          const querySnapshot = await getDocs(declarationQuery);

          if (!querySnapshot.empty) {
            declarationSnap = querySnapshot.docs[0];
          }
        }

        if (declarationSnap.exists()) {
          const data = declarationSnap.data();
          // Версията на ТОЗИ текст — заедно с него, от същия документ.
          setShownDeclarationVersion(declarationVersionOf(data as { version?: number }));
          setShownVersionPublishedAtMillis(
            toMillisOrNull(
              (data as { metadata?: { versionPublishedAt?: unknown } })?.metadata?.versionPublishedAt,
            ) ?? undefined,
          );

          // Заглавията — през общата верига резерви (`@shared/signedDocumentSource`),
          // същата, която сървърът ползва, когато снимка няма. Преписана тук, тя
          // би дала подпис с едно заглавие и документ с друго.
          setDeclarationTitleSnapshot(liveDeclarationTitle(data, currentLanguage));
          setGermanTitleSnapshot(
            currentLanguage !== 'de' ? liveGermanDeclarationTitle(data) : '',
          );

          const rawText = data.content?.[currentLanguage] || data.content?.bg || t('signModal.declarationText.unavailable');

          // Суровият текст; плейсхолдърите се попълват ПО-ДОЛУ, по целите на подписа (задача 43).
          setRawDeclarationText(rawText);

          // Load German text for legal binding (if user is not already in German)
          if (currentLanguage !== 'de' && data.content?.de) {
            setRawGermanText(data.content.de);
          } else {
            setRawGermanText('');
          }

          // Load interactive fields schema if present
          if (data.fieldsSchema?.fields && Array.isArray(data.fieldsSchema.fields)) {
            setFieldsSchema(data.fieldsSchema.fields);
          }
        } else {
          setRawDeclarationText(t('signModal.declarationText.notFound'));
        }
      } catch (err) {
        console.error('Error loading declaration:', err);
        setRawDeclarationText(t('signModal.declarationText.loadError'));
      } finally {
        setLoadingText(false);
      }
    }

    loadDeclaration();
    // `t` е в списъка, защото ефектът вече превежда резервните текстове.
    // ⚠️ Безопасно е: `useTranslation` държи `t` в React state
    // (`const [t, setT] = useState(getT)`), тоест референцията е стабилна между
    // рендери и се сменя само при истинска промяна — смяна на език или
    // зареден namespace. Ако беше нова при всеки рендер, този ефект щеше да
    // прави Firestore заявка при всеки рендер.
    // 🔴 `authLoading` е в списъка ЗАДЪЛЖИТЕЛНО: при профил без училище
    // `customClaims?.schoolId` си остава `undefined`, тоест нищо друго в списъка
    // не се мени, когато auth-ът приключи. Без него ефектът никога не се пуска
    // повторно и чакането става вечно — точно дефектът, който този клон лекува.
    // 🔴 Задача 43: `parentName` и имената на децата НЕ са в списъка — попълването е производно
    // (по-долу). Иначе всяка смяна на избора би презаредила декларацията от Firestore.
  }, [customClaims?.schoolId, authLoading, assignment.declarationCode, currentLanguage, t]);

  /**
   * 🔴 Задача 43: за кои деца ТАЗИ декларация чака подпис — зарежда се ВЕДНЪЖ при отваряне.
   *
   * Изборът на деца предлага САМО тях. Иначе „За всички" назоваваше в подписания текст и дете, чиято
   * декларация вече е подписана — цикълът на подписа го прескачаше тихо, а документът го упълномощава
   * (измерено на production: 19 подвеждащи документа, 14 семейства). Така целите ⊆ чакащите към
   * момента на зареждане. 🔴 Списъкът може да ОСТАРЕЕ, докато формата е отворена (другият родител
   * подписва) — затова подписът СВЕРЯВА целите пак и при разминаване не тръгва нищо, а списъкът се
   * презарежда (`pendingReload`). Инвариантът = зареждането + сверката, не само зареждането.
   * ⚠️ Само равенства (familyId · declarationCode · status) — без композитен индекс; същите полета
   * като заявката при подпис. 🔴 Провал = „не знам", НЕ „няма": `'failed'`, изборът не се показва и
   * подписът е забранен (празен списък би казал „нищо не чака").
   */
  const [pendingChildIds, setPendingChildIds] = useState<ReadonlySet<string> | null>(null);
  const [pendingCheckFailed, setPendingCheckFailed] = useState(false);
  /** Презарежда списъка с чакащите — след разминаване при подписа или частичен провал (задача 43). */
  const [pendingReload, setPendingReload] = useState(0);
  useEffect(() => {
    // Липсващ код на декларация печели пред чакането за claims (както при текста): няма какво да
    // се пита → „не знам", веднага, без вечно чакане.
    if (!assignment.declarationCode) {
      setPendingCheckFailed(true);
      return;
    }
    if (authLoading) return;
    let cancelled = false;
    setPendingChildIds(null);
    setPendingCheckFailed(false);
    const schoolId = customClaims?.schoolId;
    const familyId = customClaims?.familyId;
    if (!schoolId || !familyId) {
      setPendingCheckFailed(true);
      return;
    }
    (async () => {
      try {
        const snapshot = await getDocs(
          query(
            collection(db, `schools/${schoolId}/assignments`),
            where('familyId', '==', familyId),
            where('declarationCode', '==', assignment.declarationCode),
            where('status', '==', 'pending'),
          ),
        );
        if (cancelled) return;
        const ids = snapshot.docs
          .map((d) => (d.data() as { childId?: string }).childId)
          .filter((id): id is string => !!id);
        setPendingChildIds(new Set(ids));
      } catch (err) {
        console.error('Error loading pending assignments:', err);
        if (!cancelled) setPendingCheckFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, customClaims?.schoolId, customClaims?.familyId, assignment.declarationCode, pendingReload]);
  const pendingCheck: 'loading' | 'ready' | 'failed' = pendingCheckFailed
    ? 'failed'
    : pendingChildIds
      ? 'ready'
      : 'loading';
  const selectedChildNotPending =
    !!pendingChildIds &&
    !!selectedChildId &&
    children.some((c) => c.childId === selectedChildId) &&
    !pendingChildIds.has(selectedChildId);
  /**
   * „Едно дете" сочи дете, за което декларацията вече не чака. Казва го известието под избора
   * (`role=status`) — едно място; бутоните чакат друг избор, както при `nothingPending`. Иначе
   * кликът или мълчи, или повтаря същото изречение втори път (прегледът на гейта, 25.09).
   */
  const selectionNotPending = effectiveMode === 'single' && selectedChildNotPending;
  /** Текстът, изборът и бутоните чакат и списъка с чакащите — без мигане на избора. */
  const loadingDeclaration = loadingText || pendingCheck === 'loading';

  // Децата, за които декларацията ЧАКА подпис (задача 43) — докато не е известно, никои.
  const eligibleChildren = useMemo(
    () => (pendingChildIds ? children.filter((c) => pendingChildIds.has(c.childId)) : []),
    [children, pendingChildIds],
  );
  /**
   * Нищо за подписване оттук. Две лица на едно състояние, и двете с кутия и заключени бутони:
   *  - списъкът с чакащите е празен (не сечението: празно сечение може да значи и „децата не са
   *    заредени" — не знам ≠ няма);
   *  - режим „за всички", децата СА подадени, а нито едно от тях не чака — чакащото е за дете без
   *    запис (неактивно/липсващо), което оттук не може да се подпише. Без това кликът мълчеше:
   *    нито избор, нито кутия, активни бутони (прегледът, кр. 1 на поправката от 25.09).
   * В режим „едно дете" същото казва известието под избора (`selectionNotPending` или
   * „детето на възлагането липсва").
   */
  const nothingPending =
    !!pendingChildIds &&
    (pendingChildIds.size === 0 ||
      (effectiveMode === 'all' && children.length > 0 && eligibleChildren.length === 0));
  /**
   * „Изборът се промени“ — казва се чак когато е ВЯРНО: презареждането е минало и още има за какво да
   * се подпише. Паднало презареждане → говори само `pendingCheckFailed`; нищо не остава → само
   * кутията `nothingPending`. Иначе две противоречиви аларми (прегледът, кр. 1 на поправката).
   */
  const staleSelectionMessage =
    selectionWentStale && pendingCheck === 'ready' && !nothingPending ? t('signModal.errors.selectionChanged') : '';

  // GDPR Art. 8 — parental consent gate for children < 16
  // Considers ONLY the children that will actually be signed for.
  const targetChildrenForConsent = useMemo(() => {
    if (effectiveMode === 'all') return eligibleChildren;
    const picked = eligibleChildren.find((c) => c.childId === selectedChildId);
    return picked ? [picked] : [];
  }, [effectiveMode, eligibleChildren, selectedChildId]);

  /**
   * 🔴 Задача 43: `{selected_children_names}` = имената на ЦЕЛИТЕ на подписа — в показания текст и в
   * ДВЕТЕ снимки (избраният език и немският). Досега бяха ВСИЧКИ деца на семейството, независимо за
   * кои се подписва. Производно — смяната на избора обновява текста без ново зареждане.
   */
  const targetNames = useMemo(
    () => targetChildrenForConsent.map((c) => c.childName || ''),
    [targetChildrenForConsent],
  );
  const declarationText = useMemo(
    () => replaceSystemPlaceholders(rawDeclarationText, parentName, targetNames, currentLanguage),
    [rawDeclarationText, parentName, targetNames, currentLanguage],
  );
  const germanDeclarationText = useMemo(
    () => (rawGermanText ? replaceSystemPlaceholders(rawGermanText, parentName, targetNames, 'de') : ''),
    [rawGermanText, parentName, targetNames],
  );

  /**
   * 🔴 Гардът „прочетено до края" се НУЛИРА, когато показаният текст СЕ СМЕНИ (задача 43, решение):
   * гардът съществува, за да е подписаната снимка = прочетения текст, а имената са част от
   * упълномощаването. Шаблон без плейсхолдъра дава същия низ при всяка смяна на избора → без
   * нулиране. Кратък текст се маркира отново сам (`SignModal`: без скрол → прочетено).
   */
  const shownTextRef = useRef<string | null>(null);
  useEffect(() => {
    if (shownTextRef.current !== null && shownTextRef.current !== declarationText) {
      setHasScrolledToBottom(false);
    }
    shownTextRef.current = declarationText;
  }, [declarationText]);

  const { requiresParentalConsent, hasUnknownAge } = useMemo(() => {
    let hasUnknown = false;
    let hasMinor = false;
    for (const c of targetChildrenForConsent) {
      const bd = (c as unknown as { birthDate?: unknown }).birthDate;
      // Accept Firestore Timestamp, Date, or string
      let bdInput: Date | string | undefined;
      if (bd && typeof bd === 'object' && 'toDate' in bd && typeof (bd as { toDate: () => Date }).toDate === 'function') {
        bdInput = (bd as { toDate: () => Date }).toDate();
      } else if (typeof bd === 'string' || bd instanceof Date) {
        bdInput = bd;
      }
      const minor = isMinorForConsent(bdInput);
      if (minor === null) hasUnknown = true;
      else if (minor) hasMinor = true;
    }
    // Unknown age is treated as "require parental consent" (safer default)
    return { requiresParentalConsent: hasMinor || hasUnknown, hasUnknownAge: hasUnknown };
  }, [targetChildrenForConsent]);

  /**
   * 🔴 Маркираното съгласие по чл. 8 не бива да ПРЕЖИВЯВА смяната на детето.
   *
   * Сценарият, измерен при ревюто: две деца (10 и 25), режим „за всички" →
   * прагът иска съгласие → родителят маркира → превключва на „за едно дете" и
   * избира пълнолетното. Чекбоксът изчезва от екрана (блокът вече излиза рано),
   * но стойността остава `true` и се записваше при 25-годишния. Записът тогава
   * твърди „упражнявам родителски права и давам изрично съгласие по чл. 8" за
   * човек, за когото никой не е питал — тоест точно дефектът, който тази
   * задача съществува да затвори, само на друго място.
   */
  useEffect(() => {
    if (!requiresParentalConsent) {
      setGdprConsent((prev) => (prev.parentalConsent ? { parentalConsent: false } : prev));
    }
  }, [requiresParentalConsent]);

  // Handle sign action
  const handleSign = async (responseType: 'signed' | 'declined') => {
    setError('');
    setSelectionWentStale(false);
    setLoading(true);
    /** Колко подписа вече са приети в ТОВА действие — решава дали списъкът с чакащите е остарял. */
    let приети = 0;

    try {
      /**
       * 🔴 Нито подпис, нито ОТКАЗ тръгват без текста, който е бил показан.
       *
       * Измерено на production: 1 запис от 1434 носи празна снимка на текста, и
       * той е `declined` — бутонът за отказ беше достъпен, преди Firestore да
       * отговори. Родителят е отказал празен екран, а записът не казва срещу
       * какво.
       *
       * ⚠️ Това НЕ отнема правото на отказ при счупена декларация: тогава
       * `declarationText` носи четимия резервен низ (`notFound`/`loadError`),
       * тоест е непразен и минава оттук.
       *
       * ⚠️ Проверката важи и за двата отговора нарочно. Подписването днес е
       * недостижимо без текст (`hasScrolledToBottom`), но това е следствие от
       * ДРУГО изискване — оттеглят ли го някога, тази дупка се отваря наново.
       */
      if (!declarationText.trim()) {
        setError(t('signModal.errors.textNotLoaded'));
        setLoading(false);
        return;
      }

      // 🔴 Задача 43: не знаем за кои деца чака → не подписваме (и не отказваме) вслепую. След
      // проверката за текста — тя е по-конкретната причина, докато всичко още зарежда.
      if (pendingCheck !== 'ready') {
        setError(t('signModal.errors.pendingCheckFailed'));
        setLoading(false);
        return;
      }

      // Validation for signing
      if (responseType === 'signed') {
        // Check if user has scrolled to bottom of declaration text
        if (!hasScrolledToBottom) {
          setError(t('signModal.errors.scrollToEnd'));
          setLoading(false);
          return;
        }
        if (signingMethod === 'canvas' && !signatureData) {
          setError(t('signModal.errors.signatureRequired'));
          setLoading(false);
          return;
        }
        if (signingMethod === 'code' && enteredCode !== verificationCode) {
          setError(t('signModal.errors.wrongCode'));
          setLoading(false);
          return;
        }
        // Р-6.1/Р-6.10 — НЯМА общо GDPR съгласие като условие за подписване.
        // Всяка декларация носи собственото си правно основание; общ чекбокс
        // отгоре добавяше само принуда. Родител, отказал общата GDPR
        // декларация, ТРЯБВА да може да подпише медицинската, спешните
        // контакти и снимките. Единственото GDPR условие, което остава, е
        // чл. 8 — и то остава задължително.
        // GDPR Art. 8 — require parental consent when child < 16
        if (requiresParentalConsent && !gdprConsent.parentalConsent) {
          setError(t('signModal.gdprConsent.parentalConsentRequired'));
          setLoading(false);
          return;
        }

        // Check legal disclaimer acceptance
        if (!legalDisclaimerAccepted) {
          setError(t('signModal.legalBinding.confirmCheckbox'));
          setLoading(false);
          return;
        }

        // Validate interactive fields if present
        if (fieldsSchema && fieldsSchema.length > 0) {
          const errors = validateAll(fieldsSchema, fieldValues);
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setError(t('signModal.errors.fillRequiredFields'));
            setLoading(false);
            return;
          }
          setFieldErrors({});
        }
      }

      // Determine target children
      //
      // 🔴 ЕДИН източник с гарда по чл. 8. Дотук наборът се извеждаше ВТОРИ път
      // от същите входове и двата се разминаваха в един случай: `selectedChildId`,
      // който не е в `children` (остаряла селекция след презареждане на списъка).
      // Тогава `targetChildrenForConsent` е празен → `requiresParentalConsent`
      // пада на `false`, тоест клиентският гард се самоизключва, докато ТУК
      // същият id тръгваше към сървъра. Спираше го само бекендът (липсваща
      // възраст → 412) — а гардът на клиента е точно това, което задачата пази.
      const targetChildrenIds = targetChildrenForConsent.map((c) => c.childId);

      if (targetChildrenIds.length === 0) {
        // 🔴 Задача 43 §2.2: избраното дете Е в семейството, но вече не чака → казва го известието под
        // избора, не втори ред тук (бутоните са заключени — `selectionNotPending`; в „за всички“ същото
        // прави `nothingPending`).
        // ⚠️ Клонът с „Не е избрано дете" НЕ е мъртъв: детето на възлагането без запис (R2-1) или
        // още неподадени деца стигат дотук с активни бутони. Махне ли се, кликът мълчи.
        if (!selectedChildNotPending) setError(t('signModal.errors.noChildrenSelected'));
        setLoading(false);
        return;
      }

      if (!customClaims?.schoolId) {
        setError(t('signModal.errors.schoolIdMissing'));
        setLoading(false);
        return;
      }

      // Get assignment IDs paired with child data for per-child age snapshots
      const assignmentTargets: Array<{ assignmentId: string; child: Child | undefined }> = [];

      for (const childId of targetChildrenIds) {
        const assignmentsRef = collection(
          db,
          `schools/${customClaims.schoolId}/assignments`
        );
        const assignmentsQuery = query(
          assignmentsRef,
          where('familyId', '==', customClaims.familyId),
          where('childId', '==', childId),
          where('declarationCode', '==', assignment.declarationCode),
          where('status', '==', 'pending')
        );

        const assignmentsSnapshot = await getDocs(assignmentsQuery);

        if (!assignmentsSnapshot.empty) {
          assignmentTargets.push({
            assignmentId: assignmentsSnapshot.docs[0].id,
            child: eligibleChildren.find((c) => c.childId === childId),
          });
        }
      }

      // 🔴 Задача 43: целите вече не съвпадат с чакащите (подписано междувременно, напр. от другия
      // родител) → НИЩО не тръгва. Иначе подписът би отишъл за част от децата с текст, който назовава
      // всички — точно подвеждащият документ. Нищо „успешно“. Кой говори, решава ПРЕЗАРЕДЕНОТО
      // състояние — едно място, никога два реда за едно и също:
      //  - едно дете → известието под избора („вече не чака“, `role=status`);
      //  - „за всички“ → „изборът се промени“ (`role=alert`), но само ако след презареждането още има
      //    какво да се подпише (`staleSelectionMessage`); нищо не остава → кутията `nothingPending`;
      //    паднало презареждане → `pendingCheckFailed`.
      // Списъкът се презарежда, текстът следва, гардът „прочетено до края“ се нулира.
      if (assignmentTargets.length !== targetChildrenIds.length) {
        if (effectiveMode === 'all') setSelectionWentStale(true);
        setPendingReload((n) => n + 1);
        setLoading(false);
        return;
      }

      // Get auth token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const idToken = await user.getIdToken();

      // Call API for each assignment
      for (const { assignmentId, child } of assignmentTargets) {
        // Per-child age snapshot (GDPR Art. 8 audit evidence)
        const birthDateRaw = (child as unknown as { birthDate?: unknown })?.birthDate;
        let bdInput: Date | string | undefined;
        if (
          birthDateRaw &&
          typeof birthDateRaw === 'object' &&
          'toDate' in birthDateRaw &&
          typeof (birthDateRaw as { toDate: () => Date }).toDate === 'function'
        ) {
          bdInput = (birthDateRaw as { toDate: () => Date }).toDate();
        } else if (typeof birthDateRaw === 'string' || birthDateRaw instanceof Date) {
          bdInput = birthDateRaw;
        }
        const childAgeAtConsent = computeAge(bdInput) ?? undefined;
        const childBirthDateAtConsent = toBirthDateString(bdInput);

        // Р-6.11 — махнатите полета НЕ СЕ ИЗПРАЩАТ, и то не като `false`.
        // „Не е питано" ≠ „отговорил е не": изпратено `false` би изглеждало като
        // изричен отказ на нещо, което вече никой не пита. Бекендът търпи
        // липсата (`ignoreUndefinedProperties: true`, functions/src/index.ts),
        // затова новите подписи просто няма да носят полетата — а липсата им е
        // четимият белег „подписано БЕЗ изнудено съгласие", различим от отказ.
        // 🔴 Изброено изрично, без `...gdprConsent`: спред би пуснал в payload-а
        // всяко бъдещо поле на състоянието, без някой да го е искал.
        const gdprConsentForChild = {
          // Always forward the user's parentalConsent choice — backend validates
          // whether it's required (minor OR unknown age). Sending `true` for adults
          // is harmless; suppressing it for unknown-age children caused a bug.
          parentalConsent: gdprConsent.parentalConsent || undefined,
          childAgeAtConsent,
          childBirthDateAtConsent,
        };

        const response = await appCheckedFetch(
          `https://europe-west3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/createSignatureHttp`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              assignmentId,
              responseType,
              signatureData: {
                type: signingMethod,
                imageData: signingMethod === 'canvas' ? signatureData : null,
                verificationCode: signingMethod === 'code' ? verificationCode : null,
              },
              gdprConsent: gdprConsentForChild,
              // Точка 3 (Р-Е.10): ПОКАЗАНАТА версия, не текущата при изпращане.
              declarationVersion: shownDeclarationVersion,
              // Include field values if interactive fields exist
              fieldValues: fieldsSchema && fieldsSchema.length > 0 ? {
                schemaVersion: 1,
                values: fieldValues,
                isValid: true,
              } : undefined,
              // Bilingual legal binding
              legalBinding: {
                contentLanguage: currentLanguage,
                germanContentSnapshot: currentLanguage === 'de' ? declarationText : germanDeclarationText,
                selectedContentSnapshot: declarationText,
                // Р2 — заглавието се снима ОТСЕГА. Празният низ не се изпраща:
                // липсващото поле е четимият белег „подпис отпреди снимането",
                // а празен низ изглежда като „декларация без заглавие".
                ...(declarationTitleSnapshot ? { selectedTitleSnapshot: declarationTitleSnapshot } : {}),
                ...(germanTitleSnapshot ? { germanTitleSnapshot } : {}),
                legalDisclaimerAccepted: true,
                legalDisclaimerAcceptedAt: new Date().toISOString(),
                disclaimerText: t('signModal.legalBinding.disclaimerDE'),
              } satisfies LegalBinding,
            }),
          }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          // Кодът пътува с грешката, за да може да бъде ПРЕВЕДЕНА по-долу.
          // Без него оставаше само `data.message` — суровият английски текст на
          // сървъра (`createSignatureHttp.ts` го връща дословно).
          const failure = new Error(
            data.message || 'Failed to create signature',
          ) as Error & { code?: string };
          if (typeof data.error === 'string') failure.code = data.error;
          throw failure;
        }
        приети += 1;
      }

      onSuccess();
    } catch (err: unknown) {
      console.error('Error signing:', err);
      setError(translateSigningError(err, t));
      // Частичен провал (част от заявките минаха): списъкът с чакащите е остарял — презарежда се,
      // за да не тръгне повторният опит с текст, който назовава вече подписаните (задача 43).
      // 🔴 САМО тогава: без приет подпис наборът е същият, а презареждането минава през празни цели
      // и би нулирало съгласието по чл. 8 и „прочетено до края" (прегледът, кръг 2).
      if (приети > 0) setPendingReload((n) => n + 1);
    } finally {
      setLoading(false);
    }
  };

  // Computed values
  const hasInteractiveFields = Boolean(fieldsSchema && fieldsSchema.length > 0);

  return {
    mode: effectiveMode,
    setMode,
    allowAllChildren,
    selectedChildId,
    setSelectedChildId,
    signatureData,
    setSignatureData,
    signingMethod,
    setSigningMethod,
    verificationCode,
    enteredCode,
    setEnteredCode,
    gdprConsent,
    setGdprConsent,
    loading,
    error: error || staleSelectionMessage,
    declarationText,
    /**
     * Живото заглавие на езика на четеца — СЪЩАТА стойност, която отива в
     * `selectedTitleSnapshot` при подписване. Заглавката на модала показваше
     * `assignment.declarationTitle.bg`, тоест българското име отпреди
     * преименуването, докато подписът записваше това. Родителят вижда точно
     * каквото ще бъде записано.
     */
    declarationTitleSnapshot,
    // 🔴 Двете за ЕКРАНА, не за изпращане: `SignModal` разгръща `{{version}}` при
    // РЕНДЕР. Състоянието и снимката остават СУРОВИ — снимката е доказателство
    // какво е видял родителят и трябва да може да се ВЪЗСТАНОВИ, не да се преписва.
    shownDeclarationVersion,
    shownVersionPublishedAtMillis,
    loadingDeclaration,
    pendingCheck,
    selectedChildNotPending,
    selectionNotPending,
    nothingPending,
    // Interactive Fields
    fieldsSchema,
    fieldValues,
    setFieldValues,
    fieldErrors,
    // Scroll tracking
    hasScrolledToBottom,
    setHasScrolledToBottom,
    // Legal binding (bilingual)
    germanDeclarationText,
    legalDisclaimerAccepted,
    setLegalDisclaimerAccepted,
    currentLanguage,
    // Computed
    eligibleChildren,
    hasInteractiveFields,
    requiresParentalConsent,
    hasUnknownAge,
    // Actions
    handleSign,
  };
}
